import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  Clock,
  Phone,
  Mail,
  MapPin,
  FileText,
  Calendar,
  User,
  Building,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const statusConfig = {
    confirmed: { label: 'Confirmed', className: 'bg-blue-100 text-blue-700', icon: CheckCircle },
    processing: { label: 'Processing', className: 'bg-yellow-100 text-yellow-700', icon: Clock },
    shipped: { label: 'Shipped', className: 'bg-purple-100 text-purple-700', icon: Truck },
    delivered: { label: 'Delivered', className: 'bg-green-100 text-green-700', icon: Package },
    completed: { label: 'Completed', className: 'bg-gray-100 text-gray-700', icon: CheckCircle },
    cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700', icon: null },
  };
  
  const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.className} px-3 py-1`}>
      {Icon && <Icon className="w-4 h-4 mr-1" />}
      {config.label}
    </Badge>
  );
};

const Timeline = ({ timeline }) => {
  if (!timeline || timeline.length === 0) return null;
  
  return (
    <div className="space-y-4">
      {timeline.map((entry, index) => (
        <div key={index} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-blue-500' : 'bg-gray-300'}`} />
            {index < timeline.length - 1 && (
              <div className="w-0.5 flex-1 bg-gray-200 my-1" />
            )}
          </div>
          <div className="flex-1 pb-4">
            <p className="font-medium capitalize">{entry.status?.replace('_', ' ')}</p>
            <p className="text-sm text-gray-500">
              {new Date(entry.timestamp).toLocaleString()}
            </p>
            {entry.note && (
              <p className="text-sm text-gray-600 mt-1">{entry.note}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function BookingDetailsPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  useEffect(() => {
    const fetchBooking = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/business-services/bookings/${bookingId}`);
        const data = await response.json();
        
        if (data.success) {
          setBooking(data.booking);
        } else {
          toast.error('Booking not found');
        }
      } catch (error) {
        console.error('Failed to fetch booking:', error);
        toast.error('Failed to load booking');
      } finally {
        setLoading(false);
      }
    };
    
    if (bookingId) {
      fetchBooking();
    }
  }, [bookingId]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold mb-2">Booking Not Found</h2>
            <Button onClick={() => navigate('/my-rfqs')}>View My RFQs</Button>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="booking-details-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 font-mono">{booking.booking_number}</p>
            <h1 className="text-2xl font-bold">Booking Details</h1>
            <p className="text-gray-500">
              Created on {new Date(booking.created_at).toLocaleDateString()}
            </p>
          </div>
          <StatusBadge status={booking.status} />
        </div>
        
        {/* Order Summary Card */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 mb-1">Order Total</p>
                <p className="text-3xl font-bold text-blue-700">
                  {currencySymbol}{booking.total_amount?.toLocaleString()}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  {booking.quantity} {booking.quantity_unit}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">RFQ Reference</p>
                <p className="font-mono text-sm">{booking.rfq_number}</p>
                <p className="font-mono text-sm text-gray-500">{booking.quote_number}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Supplier Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Supplier
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-semibold text-lg">{booking.provider_name}</p>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="w-4 h-4" />
                {booking.provider_email}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4" />
                {booking.provider_phone}
              </div>
            </CardContent>
          </Card>
          
          {/* Delivery Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Delivery
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {booking.delivery_date && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>Expected: {new Date(booking.delivery_date).toLocaleDateString()}</span>
                </div>
              )}
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                <span>{booking.delivery_address || 'Address not specified'}</span>
              </div>
              {booking.delivery_cost > 0 && (
                <p className="text-sm text-gray-600">
                  Delivery cost: {currencySymbol}{booking.delivery_cost}
                </p>
              )}
            </CardContent>
          </Card>
          
          {/* Customer Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Your Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-semibold">{booking.customer_name}</p>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="w-4 h-4" />
                {booking.customer_email}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4" />
                {booking.customer_phone}
              </div>
            </CardContent>
          </Card>
          
          {/* Order Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Order Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600">{booking.item_details}</p>
              {booking.special_instructions && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Special Instructions</p>
                  <p className="text-sm">{booking.special_instructions}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Order Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Timeline timeline={booking.timeline} />
          </CardContent>
        </Card>
        
        {/* Terms & Conditions */}
        {booking.terms_conditions && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Terms & Conditions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {booking.terms_conditions}
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Actions */}
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => navigate('/my-rfqs')}>
            View All RFQs
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            Print Details
          </Button>
        </div>
      </div>
    </div>
  );
}
